/** @format */

/* =========================================================
   SmartFlow Workflow Builder — types & catalogues
   Ported from the SmartFlow prototype, typed for Angular.

   Field types (FieldType / FieldTypeMeta / FIELD_TYPES) now derive from the
   shared data-point types, the single source of truth shared with the
   form-field creator. They are re-exported here so existing workflow-builder
   imports stay unchanged.
   ========================================================= */

export {
  FieldType,
  FieldTypeMeta,
  FIELD_TYPES,
  panelFieldTypes,
} from 'src/app/models/data-point-types';
import { FieldType, FIELD_TYPES } from 'src/app/models/data-point-types';

export type ActionType =
  | 'accept'
  | 'approve'
  | 'reject'
  | 'comment'
  | 'escalate'
  | 'submit';

export type WorkflowStatus = 'DRAFT' | 'PUBLISHED' | 'INACTIVE';
export type WorkflowTier = 'Managed' | 'Automated';
export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'PUBLISH'
  | 'DEACTIVATE'
  | 'EXPORT_AUDIT_LOG';

export interface ActionTypeMeta {
  label: string;
  color: 'green' | 'red' | 'grey';
  icon: string;
}

export const ACTION_TYPES: Record<ActionType, ActionTypeMeta> = {
  accept: { label: 'Accept', color: 'green', icon: 'check' },
  approve: { label: 'Approve', color: 'green', icon: 'check-check' },
  reject: { label: 'Reject', color: 'red', icon: 'x' },
  comment: { label: 'Comment & Advance', color: 'grey', icon: 'message-square' },
  escalate: { label: 'Escalate', color: 'grey', icon: 'flag' },
  submit: { label: 'Submit', color: 'green', icon: 'send' },
};

export const ROLES = [
  'Initiator',
  'First Approver',
  'Second Approver',
];

/* ---------- Configuration sub-objects ---------- */
export interface NumberCfg { min: number | null; max: number | null; decimals: number; }
export interface DateRangeCfg { startLabel: string; endLabel: string; }
export interface FileCfg { types: string; maxFiles: number; maxSizeMB: number; perFileNotes: boolean; }
export interface DurationCfg { startFieldId: string; endFieldId: string; unit: string; }
export interface RefnumCfg { prefix: string; padding: number; }

export interface Condition { join: 'AND' | 'OR'; fieldId: string; op: string; value: string; }
export interface Visibility { enabled: boolean; conditions: Condition[]; }
export interface Guard { enabled: boolean; keys: string[]; }

export interface Field {
  id: string;
  type: FieldType;
  label: string;
  placeholder: string;
  required: boolean;
  options: string[] | null;
  /** For dropdown fields: single-select list, multi-select list, or dropdown list. */
  dropdownVariant?: DropdownVariant;
  numberCfg: NumberCfg | null;
  dateRangeCfg: DateRangeCfg | null;
  fileCfg: FileCfg | null;
  durationCfg: DurationCfg | null;
  refnumCfg: RefnumCfg | null;
  visibility: Visibility;
  guard: Guard;
}

export type DropdownVariant = 'single' | 'multi' | 'dropdown';

export interface ActionBtn {
  id: string;
  type: ActionType;
  label: string;
  requireComment: boolean;
  commentPrompt: string;
}

/** A mirror reference to a Stage 1 field (read-only inherited copy). */
export interface MirrorEntry { kind: 'mirror'; refId: string; }
/** A field captured natively at a later stage. */
export interface NativeEntry { kind: 'native'; field: Field; }
/** Stage 1 stores raw Field objects; later stages store entries. */
export type StageEntry = Field | MirrorEntry | NativeEntry;

/** A single stage-notification rule = channel x trigger x recipient role. */
export type NotificationChannel = 'in_app' | 'email';
export type NotificationTrigger = 'entry' | 'exit' | 'escalation';
export interface NotificationRule {
  id: string;
  channel: NotificationChannel;
  trigger: NotificationTrigger;
  recipientRole: string;
}

export interface Notifications {
  transition: boolean;
  perItem: boolean;
  reminder: boolean;
  escalation: boolean;
  perItemRecipientField: string;
  perItemEventLabel: string;
  reminderSchedule: string;
  reminderTime: string;
  rules: NotificationRule[];
}

export function makeNotificationRule(): NotificationRule {
  return {
    id: 'nr_' + Math.random().toString(36).slice(2, 9),
    channel: 'in_app',
    trigger: 'entry',
    recipientRole: '',
  };
}

export interface TimeBudget { value: number; unit: string; }
export interface ConditionalRoute { enabled: boolean; conditions: Condition[]; }
export interface Retention { store: boolean; days: number; autoPurge: boolean; }

export type ApprovalTargetType = 'next' | 'stage' | 'close' | 'output';
export type RejectionTargetType = '' | 'previous' | 'close';
export interface ApprovalTarget { type: ApprovalTargetType; stage: number | null; }
export interface RejectionTarget { type: RejectionTargetType; }

export interface Routing {
  actorRoles: string[];
  notifications: Notifications;
  timeBudget: TimeBudget;
  escalationTarget: string;
  conditionalRoute: ConditionalRoute;
  immutable: boolean;
  retention: Retention;
  secondApprover: boolean;
  approvalTarget: ApprovalTarget;
  rejectionTarget: RejectionTarget;
}

export interface Stage {
  name?: string;
  rows: StageEntry[][];
  hiddenInherited: Set<string>;
  actions: ActionBtn[];
  routing: Routing;
}

export interface FinalOutputs {
  pdf: { enabled: boolean; template: string };
  email: { enabled: boolean; recipients: string[] };
}

export interface AuditEntry {
  id: string;
  action: AuditAction;
  entityType: string;
  entityId: string;
  userId: string;
  ts: string;
  before: any;
  after: any;
  reason: string | null;
  sessionId: string;
  ip: string;
}

export interface ValidationRule {
  stage: number;
  label: string;
  pass: boolean;
  failMessage: string;
  target: () => void;
}

export interface ValidationResult {
  passed: boolean;
  ts: string;
  rules: ValidationRule[];
}

export interface Workflow {
  name: string;
  sku: string;
  tier: WorkflowTier;
  stageCount: number;
  activeStage: number;
  stages: { [index: number]: Stage };
  status: WorkflowStatus;
  version: number;
  finalOutputs: FinalOutputs;
  inFlightCount: number;
  lastValidation: ValidationResult | null;
  dirty: boolean;
}

/* ---------- Metadata handed off from the create page (Page 1) ---------- */
export interface WorkflowMeta {
  name: string;
  sku: string;
  tier: WorkflowTier;
  stageCount: number;
}

/* ---------- Factories ---------- */
function rid(prefix: string): string {
  return prefix + Math.random().toString(36).slice(2, 9);
}

export function makeField(type: FieldType): Field {
  const t = FIELD_TYPES[type];
  const options = type === 'dropdown' ? ['Option 1', 'Option 2', 'Option 3'] : null;
  return {
    id: rid('f_'),
    type,
    // Dropdown card name defaults to its first option.
    label: type === 'dropdown' && options ? options[0] : t.defaultLabel,
    placeholder: '',
    required: false,
    options,
    dropdownVariant: type === 'dropdown' ? 'dropdown' : undefined,
    numberCfg: type === 'number' ? { min: null, max: null, decimals: 0 } : null,
    dateRangeCfg: type === 'daterange' ? { startLabel: 'Start Date', endLabel: 'End Date' } : null,
    fileCfg: type === 'file' ? { types: 'any', maxFiles: 5, maxSizeMB: 25, perFileNotes: false } : null,
    durationCfg: type === 'duration' ? { startFieldId: '', endFieldId: '', unit: 'days' } : null,
    refnumCfg: type === 'refnum' ? { prefix: 'REF-', padding: 4 } : null,
    visibility: { enabled: false, conditions: [] },
    guard: { enabled: false, keys: [] },
  };
}

export function makeAction(type: ActionType): ActionBtn {
  return {
    id: rid('a_'),
    type,
    label: ACTION_TYPES[type].label,
    requireComment: type === 'reject',
    commentPrompt: '',
  };
}

export function blankStage(idx: number): Stage {
  const isFirst = idx === 1;
  return {
    rows: [],
    hiddenInherited: new Set<string>(),
    actions: isFirst
      ? [makeAction('submit')]
      : [makeAction('accept'), makeAction('reject'), makeAction('comment')],
    routing: {
      actorRoles: [idx === 1 ? 'Initiator' : idx === 2 ? 'First Approver' : 'Second Approver'],
      notifications: {
        transition: true,
        perItem: false,
        reminder: false,
        escalation: false,
        perItemRecipientField: '',
        perItemEventLabel: '',
        reminderSchedule: 'daily',
        reminderTime: '09:30',
        rules: [],
      },
      timeBudget: { value: idx === 1 ? 0 : 1, unit: idx === 1 ? 'minutes' : 'days' },
      escalationTarget: '',
      conditionalRoute: { enabled: false, conditions: [] },
      immutable: false,
      retention: { store: true, days: 365, autoPurge: false },
      secondApprover: false,
      approvalTarget: { type: 'next', stage: null },
      rejectionTarget: { type: '' },
    },
  };
}
